
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Landmark, ArrowRight, X, Loader2, LogIn, UserPlus,
  Check, Fingerprint, User, CheckCircle, Gamepad2, Copy, Wifi,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { createBankAccount, createPrincipalBankAccount, generateMcLinkCode, getBankData, getMyBankAccounts, getTransactions } from '@/lib/actions';
import { BancoClient } from '@/components/banco/BancoClient';
import { BankAccount, Transaction } from '@/lib/types';
import { useAuth } from '@/hooks/useAuth';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase/config';

// ─── Web Audio utilities ──────────────────────────────────────────────────────

const makeCtx = () => {
  const W = window as any;
  return new (W.AudioContext || W.webkitAudioContext)() as AudioContext;
};

const playKeyTone = () => {
  try {
    const ctx = makeCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(680, ctx.currentTime);
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.075);
    osc.start();
    osc.stop(ctx.currentTime + 0.075);
    setTimeout(() => ctx.close(), 300);
  } catch { /* no-op */ }
};

const playSuccessSound = () => {
  try {
    const ctx = makeCtx();
    [523.25, 659.25, 783.99].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'sine';
      osc.frequency.value = freq;
      const t = ctx.currentTime + i * 0.12;
      gain.gain.setValueAtTime(0.18, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
      osc.start(t);
      osc.stop(t + 0.2);
    });
    setTimeout(() => ctx.close(), 1000);
  } catch { /* no-op */ }
};

const playErrorSound = () => {
  try {
    const ctx = makeCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(260, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(100, ctx.currentTime + 0.35);
    gain.gain.setValueAtTime(0.14, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.35);
    osc.start();
    osc.stop(ctx.currentTime + 0.35);
    setTimeout(() => ctx.close(), 600);
  } catch { /* no-op */ }
};

// ─── Particle canvas ──────────────────────────────────────────────────────────

const ParticleCanvas = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    type P = { x: number; y: number; vx: number; vy: number; r: number; a: number };
    const pts: P[] = Array.from({ length: 60 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.38,
      vy: (Math.random() - 0.5) * 0.38,
      r: Math.random() * 2.8 + 0.8,
      a: Math.random() * 0.55 + 0.08,
    }));

    let raf: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < pts.length; i++) {
        const p = pts[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        else if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        else if (p.y > canvas.height) p.y = 0;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(234,179,8,${p.a})`;
        ctx.fill();

        for (let j = i + 1; j < pts.length; j++) {
          const q = pts[j];
          const dx = p.x - q.x;
          const dy = p.y - q.y;
          const d = Math.sqrt(dx * dx + dy * dy);
          if (d < 125) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(q.x, q.y);
            ctx.strokeStyle = `rgba(234,179,8,${0.13 * (1 - d / 125)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" />;
};

// ─── PIN components ───────────────────────────────────────────────────────────

const PinDots = ({ len }: { len: number }) => (
  <div className="flex gap-3 justify-center">
    {Array.from({ length: 6 }).map((_, i) => (
      <motion.div
        key={i}
        animate={{ scale: len > i ? 1.3 : 1 }}
        transition={{ type: 'spring', stiffness: 600, damping: 25 }}
        className={cn(
          'h-3 w-3 rounded-full border-2 border-primary transition-colors duration-100',
          len > i ? 'bg-primary' : '',
        )}
      />
    ))}
  </div>
);

const PinKeypad = ({ pin, onChange }: { pin: string; onChange: (p: string) => void }) => {
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'];

  const press = (k: string) => {
    playKeyTone();
    if (k === '⌫') onChange(pin.slice(0, -1));
    else if (pin.length < 6) onChange(pin + k);
  };

  return (
    <div className="space-y-4">
      <PinDots len={pin.length} />
      <div className="grid grid-cols-3 gap-2 w-52 mx-auto">
        {keys.map((k, idx) => (
          <motion.button
            key={idx}
            type="button"
            whileTap={k ? { scale: 0.88 } : {}}
            onClick={() => k && press(k)}
            className={cn(
              'h-14 text-xl font-bold rounded-2xl select-none transition-colors',
              k
                ? 'bg-white/5 border border-white/10 hover:bg-primary/20 hover:text-primary cursor-pointer'
                : 'invisible pointer-events-none',
              k === '⌫' && 'text-base text-muted-foreground',
            )}
            disabled={!k}
          >
            {k}
          </motion.button>
        ))}
      </div>
    </div>
  );
};

// ─── Login panel ──────────────────────────────────────────────────────────────

const LoginPanel = ({
  onSuccess,
  onBack,
}: {
  onSuccess: (a: BankAccount) => void;
  onBack: () => void;
}) => {
  const [name, setName] = useState('');
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [shake, setShake] = useState(false);
  const { toast } = useToast();
  const submitted = useRef(false);

  const doLogin = useCallback(async (n: string, p: string) => {
    if (submitted.current || loading) return;
    submitted.current = true;
    setLoading(true);
    const res = await getBankData(n, p);
    if (res.success && res.account) {
      playSuccessSound();
      onSuccess(res.account as BankAccount);
    } else {
      playErrorSound();
      setShake(true);
      setPin('');
      setTimeout(() => { setShake(false); submitted.current = false; }, 600);
      toast({ title: 'Acceso denegado', description: res.message, variant: 'destructive' });
    }
    setLoading(false);
  }, [loading, onSuccess, toast]);

  // Auto-login when PIN is complete and name is filled
  useEffect(() => {
    if (pin.length === 6 && name.trim()) {
      doLogin(name.trim(), pin);
    }
  }, [pin]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -24 }}
      className="w-full max-w-sm mx-auto"
    >
      <div className="relative bg-card/80 backdrop-blur-2xl border border-white/10 rounded-3xl p-8 shadow-2xl shadow-black/40 space-y-6">
        <button
          onClick={onBack}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 text-muted-foreground transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="text-center">
          <div className="inline-flex p-3 rounded-2xl bg-primary/10 mb-3">
            <Landmark className="h-7 w-7 text-primary" />
          </div>
          <h2 className="text-xl font-bold">Acceder a tu cuenta</h2>
          <p className="text-sm text-muted-foreground mt-1">Introduce tu nombre y PIN de seguridad</p>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs uppercase tracking-widest text-muted-foreground">Nombre de cuenta</Label>
          <Input
            placeholder="Mi cuenta principal"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && name.trim() && pin.length === 6 && doLogin(name.trim(), pin)}
            className="h-11 bg-white/5 border-white/10 rounded-xl"
            autoFocus
          />
        </div>

        <motion.div animate={shake ? { x: [-10, 10, -8, 8, -4, 4, 0] } : {}} transition={{ duration: 0.5 }}>
          <Label className="text-xs uppercase tracking-widest text-muted-foreground block mb-3 text-center">
            PIN de seguridad
          </Label>
          <PinKeypad pin={pin} onChange={setPin} />
        </motion.div>

        <Button
          onClick={() => name.trim() && pin.length === 6 && doLogin(name.trim(), pin)}
          disabled={loading || !name.trim() || pin.length !== 6}
          className="w-full h-12 rounded-2xl font-semibold"
        >
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <LogIn className="mr-2 h-4 w-4" />}
          Entrar
        </Button>
      </div>
    </motion.div>
  );
};

// ─── Create account flow ──────────────────────────────────────────────────────

const CreateAccountFlow = ({ onClose }: { onClose: () => void }) => {
  const [step, setStep] = useState(1);
  const [accountName, setAccountName] = useState('');
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [pinPhase, setPinPhase] = useState<'create' | 'confirm'>('create');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // When create-PIN is full, auto-advance to confirm
  useEffect(() => {
    if (step === 3 && pinPhase === 'create' && pin.length === 6) {
      setTimeout(() => setPinPhase('confirm'), 250);
    }
  }, [pin, step, pinPhase]);

  // When confirm PIN is full, auto-validate
  useEffect(() => {
    if (step === 3 && pinPhase === 'confirm' && pinConfirm.length === 6) {
      if (pinConfirm === pin) {
        setStep(4);
      } else {
        playErrorSound();
        toast({ title: 'Los PINs no coinciden', description: 'Vuelve a introducirlo.', variant: 'destructive' });
        setPinConfirm('');
        setPinPhase('create');
        setPin('');
      }
    }
  }, [pinConfirm, pin, step, pinPhase, toast]);

  const advance = async () => {
    if (step === 2 && !accountName.trim()) {
      toast({ title: 'Campo requerido', description: 'El nombre no puede estar vacío.', variant: 'destructive' });
      return;
    }
    if (step < 4) { setStep(s => s + 1); return; }

    setLoading(true);
    const res = await createBankAccount(accountName.trim(), pin);
    if (res.success) {
      playSuccessSound();
      setStep(5);
    } else {
      playErrorSound();
      toast({ title: 'Error', description: res.message, variant: 'destructive' });
    }
    setLoading(false);
  };

  const goBack = () => {
    if (step === 3 && pinPhase === 'confirm') {
      setPinPhase('create');
      setPinConfirm('');
      setPin('');
    } else {
      setStep(s => Math.max(1, s - 1));
    }
  };

  const STEPS = ['Información', 'Nombre', 'PIN', 'Confirmar'];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-background/96 backdrop-blur flex flex-col"
    >
      <header className="border-b border-white/10 bg-card/50 backdrop-blur">
        <div className="container mx-auto px-6 h-16 flex justify-between items-center">
          <div className="flex items-center gap-2 text-xl font-bold text-primary">
            <Landmark className="h-5 w-5" /> JartiBank
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4 mr-1" /> Salir
          </Button>
        </div>
      </header>

      <div className="flex-1 flex overflow-hidden">
        {/* Progress sidebar */}
        <aside className="hidden md:flex flex-col justify-center px-10 py-8 bg-card/20 border-r border-white/8 w-56 shrink-0">
          <p className="text-[0.65rem] uppercase tracking-widest opacity-50 mb-8">Progreso</p>
          <div className="space-y-5">
            {STEPS.map((label, i) => {
              const n = i + 2;
              const done = step > n;
              const active = step === n;
              return (
                <div key={i} className="flex items-center gap-3">
                  <div className={cn(
                    'h-7 w-7 rounded-full border-2 flex items-center justify-center text-xs font-bold shrink-0',
                    done ? 'bg-primary border-primary text-primary-foreground' : active ? 'border-primary text-primary' : 'border-white/20 text-muted-foreground',
                  )}>
                    {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
                  </div>
                  <span className={cn('text-sm', active ? 'text-foreground font-medium' : 'text-muted-foreground')}>{label}</span>
                </div>
              );
            })}
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 flex flex-col items-center justify-center p-8 overflow-y-auto">
          <div className="w-full max-w-md space-y-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={`${step}-${pinPhase}`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.22 }}
              >
                {step === 1 && (
                  <div className="space-y-6">
                    <h2 className="text-3xl font-bold">¡Abre tu cuenta JartiBank!</h2>
                    <p className="text-muted-foreground">El proceso tarda menos de 2 minutos.</p>
                    <div className="space-y-3">
                      {[
                        { icon: User, title: 'Elige un nombre único', desc: 'Un alias para identificar fácilmente tu cuenta.' },
                        { icon: Fingerprint, title: 'Crea tu PIN secreto', desc: 'Un código de 6 dígitos que solo tú conoces.' },
                      ].map(({ icon: Icon, title, desc }, i) => (
                        <div key={i} className="flex items-start gap-4 p-4 rounded-2xl bg-white/5 border border-white/8">
                          <div className="p-2 rounded-xl bg-primary/10 text-primary shrink-0"><Icon className="h-5 w-5" /></div>
                          <div><p className="font-semibold">{title}</p><p className="text-sm text-muted-foreground">{desc}</p></div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="text-3xl font-bold">Nombre de cuenta</h2>
                      <p className="text-muted-foreground mt-1">Elige un alias para identificarla.</p>
                    </div>
                    <Input
                      placeholder="Ej: Cuenta Principal, Ahorros..."
                      value={accountName}
                      onChange={e => setAccountName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && advance()}
                      className="h-12 text-base bg-white/5 border-white/10 rounded-xl"
                      autoFocus
                    />
                  </div>
                )}

                {step === 3 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="text-3xl font-bold">
                        {pinPhase === 'create' ? 'Crea tu PIN' : 'Confirma tu PIN'}
                      </h2>
                      <p className="text-muted-foreground mt-1">
                        {pinPhase === 'create' ? 'Elige 6 dígitos secretos.' : 'Repítelo para confirmar.'}
                      </p>
                    </div>
                    <PinKeypad
                      pin={pinPhase === 'create' ? pin : pinConfirm}
                      onChange={pinPhase === 'create' ? setPin : setPinConfirm}
                    />
                  </div>
                )}

                {step === 4 && (
                  <div className="space-y-5">
                    <div>
                      <h2 className="text-3xl font-bold">¡Casi listo!</h2>
                      <p className="text-muted-foreground mt-1">Revisa los datos y confirma.</p>
                    </div>
                    <div className="p-5 rounded-2xl bg-white/5 border border-white/8 space-y-3">
                      {[['Nombre', accountName], ['PIN', '●●●●●●']].map(([k, v]) => (
                        <div key={k} className="flex justify-between">
                          <span className="text-muted-foreground">{k}</span>
                          <span className="font-semibold">{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {step === 5 && (
                  <div className="text-center space-y-6">
                    <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 180, damping: 14 }}>
                      <CheckCircle className="h-20 w-20 text-green-500 mx-auto" />
                    </motion.div>
                    <div>
                      <h2 className="text-3xl font-bold">¡Cuenta creada!</h2>
                      <p className="text-muted-foreground mt-2">Ya puedes iniciar sesión con tu nombre y PIN.</p>
                    </div>
                    <Button onClick={onClose} size="lg" className="w-full rounded-2xl">Ir a inicio</Button>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>

            {step < 5 && step !== 3 && (
              <div className="flex justify-between pt-2">
                {step > 1
                  ? <Button variant="ghost" onClick={goBack}>Atrás</Button>
                  : <div />}
                <Button onClick={advance} disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {step === 4 ? 'Crear cuenta' : 'Siguiente'}
                  {step < 4 && <ArrowRight className="ml-2 h-4 w-4" />}
                </Button>
              </div>
            )}
            {step === 3 && (
              <div className="flex justify-start pt-2">
                <Button variant="ghost" onClick={goBack}>Atrás</Button>
              </div>
            )}
          </div>
        </main>
      </div>
    </motion.div>
  );
};

// ─── Create PRINCIPAL account flow (vinculación con Minecraft) ───────────────

const CreatePrincipalAccountFlow = ({ onClose }: { onClose: () => void }) => {
  const { user } = useAuth();
  const [step, setStep] = useState<'intro' | 'code' | 'linked' | 'name' | 'pin' | 'pinConfirm' | 'success'>('intro');
  const [code, setCode] = useState('');
  const [isLinked, setIsLinked] = useState(false);
  const [mcInfo, setMcInfo] = useState<{ uuid?: string; name?: string }>({});
  const [accountName, setAccountName] = useState('');
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (!user?.uid || step !== 'code') return;
    const unsub = onSnapshot(doc(db, 'users', user.uid), (snap) => {
      const data = snap.data();
      if (data?.linked && data?.mc_uuid) {
        setIsLinked(true);
        setMcInfo({ uuid: data.mc_uuid, name: data.name });
        playSuccessSound();
        setStep('linked');
      }
    });
    return () => unsub();
  }, [user?.uid, step]);

  const handleGenerateCode = async () => {
    setLoading(true);
    const res = await generateMcLinkCode();
    if (res.success && res.code) {
      setCode(res.code);
      setStep('code');
    } else {
      toast({ title: 'Error', description: res.message, variant: 'destructive' });
    }
    setLoading(false);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    toast({ title: 'Código copiado', description: code });
  };

  useEffect(() => {
    if (step === 'pin' && pin.length === 6) setTimeout(() => setStep('pinConfirm'), 250);
  }, [pin, step]);

  useEffect(() => {
    if (step === 'pinConfirm' && pinConfirm.length === 6) {
      if (pinConfirm === pin) {
        handleCreate();
      } else {
        playErrorSound();
        toast({ title: 'Los PINs no coinciden', variant: 'destructive' });
        setPinConfirm('');
        setPin('');
        setStep('pin');
      }
    }
  }, [pinConfirm]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreate = async () => {
    setLoading(true);
    const res = await createPrincipalBankAccount(accountName.trim(), pin);
    if (res.success) {
      playSuccessSound();
      setStep('success');
    } else {
      playErrorSound();
      toast({ title: 'Error', description: res.message, variant: 'destructive' });
      setStep('linked');
    }
    setLoading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-background/96 backdrop-blur flex flex-col"
    >
      <header className="border-b border-white/10 bg-card/50 backdrop-blur">
        <div className="container mx-auto px-6 h-16 flex justify-between items-center">
          <div className="flex items-center gap-2 text-xl font-bold text-amber-400">
            <Gamepad2 className="h-5 w-5" /> Cuenta Principal
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-4 w-4 mr-1" /> Salir
          </Button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md space-y-8">
          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.22 }}>
              {step === 'intro' && (
                <div className="space-y-6">
                  <h2 className="text-3xl font-bold">Vincula tu cuenta con Minecraft</h2>
                  <p className="text-muted-foreground">
                    Las cuentas principales comparten saldo en tiempo real con tu jugador dentro del servidor. Necesitas estar conectado al servidor de Minecraft para completar este proceso.
                  </p>
                  <div className="flex items-start gap-4 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20">
                    <Wifi className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-muted-foreground">Conéctate al servidor antes de continuar para poder ejecutar el comando de vinculación.</p>
                  </div>
                  <Button onClick={handleGenerateCode} disabled={loading} className="w-full h-12 rounded-2xl font-semibold">
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Generar código de vinculación
                  </Button>
                </div>
              )}

              {step === 'code' && (
                <div className="space-y-6 text-center">
                  <h2 className="text-2xl font-bold">Tu código de vinculación</h2>
                  <p className="text-muted-foreground">Conéctate al servidor y escribe en el chat:</p>
                  <div className="p-4 rounded-2xl bg-white/5 border border-white/10 font-mono text-xs text-left">
                    /vincular {code}
                  </div>
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-4xl font-black tracking-[0.3em] text-amber-400">{code}</span>
                    <Button size="icon" variant="ghost" onClick={copyCode}><Copy className="h-4 w-4" /></Button>
                  </div>
                  <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" /> Esperando confirmación del servidor...
                  </p>
                </div>
              )}

              {step === 'linked' && (
                <div className="space-y-6 text-center">
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', stiffness: 200 }}>
                    <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
                  </motion.div>
                  <h2 className="text-2xl font-bold">¡Vinculado!</h2>
                  <p className="text-muted-foreground">Jugador: <strong className="text-foreground">{mcInfo.name || mcInfo.uuid}</strong></p>
                  <Button onClick={() => setStep('name')} className="w-full h-12 rounded-2xl font-semibold">
                    Continuar <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}

              {step === 'name' && (
                <div className="space-y-5">
                  <div>
                    <h2 className="text-3xl font-bold">Nombre de la cuenta</h2>
                    <p className="text-muted-foreground mt-1">Elige un alias para tu cuenta principal.</p>
                  </div>
                  <Input
                    placeholder="Ej: Mi Personaje"
                    value={accountName}
                    onChange={e => setAccountName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && accountName.trim() && setStep('pin')}
                    className="h-12 text-base bg-white/5 border-white/10 rounded-xl"
                    autoFocus
                  />
                  <Button onClick={() => accountName.trim() && setStep('pin')} disabled={!accountName.trim()} className="w-full h-12 rounded-2xl font-semibold">
                    Siguiente <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </div>
              )}

              {(step === 'pin' || step === 'pinConfirm') && (
                <div className="space-y-5">
                  <div>
                    <h2 className="text-3xl font-bold">{step === 'pin' ? 'Crea tu PIN' : 'Confirma tu PIN'}</h2>
                    <p className="text-muted-foreground mt-1">{step === 'pin' ? 'Elige 6 dígitos secretos.' : 'Repítelo para confirmar.'}</p>
                  </div>
                  <PinKeypad pin={step === 'pin' ? pin : pinConfirm} onChange={step === 'pin' ? setPin : setPinConfirm} />
                </div>
              )}

              {step === 'success' && (
                <div className="text-center space-y-6">
                  <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: 'spring', stiffness: 180, damping: 14 }}>
                    <CheckCircle className="h-20 w-20 text-green-500 mx-auto" />
                  </motion.div>
                  <div>
                    <h2 className="text-3xl font-bold">¡Cuenta principal creada!</h2>
                    <p className="text-muted-foreground mt-2">Ya puedes iniciar sesión con tu nombre y PIN.</p>
                  </div>
                  <Button onClick={onClose} size="lg" className="w-full rounded-2xl">Ir a inicio</Button>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </main>
    </motion.div>
  );
};

// ─── My accounts quick-access list ───────────────────────────────────────────

const MyAccountCard = ({ account, onSelect }: { account: BankAccount; onSelect: () => void }) => {
  const isPrincipal = account.accountType === 'principal';
  return (
    <motion.button
      whileHover={{ y: -3 }}
      whileTap={{ scale: 0.98 }}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-3 rounded-2xl border p-4 text-left transition-colors',
        isPrincipal ? 'border-amber-500/30 bg-amber-500/10 hover:bg-amber-500/15' : 'border-sky-500/30 bg-sky-500/10 hover:bg-sky-500/15'
      )}
    >
      <div className={cn('flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl', isPrincipal ? 'bg-amber-500/20 text-amber-400' : 'bg-sky-500/20 text-sky-400')}>
        {isPrincipal ? <Gamepad2 className="h-5 w-5" /> : <UserPlus className="h-5 w-5" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-semibold">{account.accountName}</p>
        <p className="text-xs text-muted-foreground">
          {isPrincipal ? 'Cuenta Principal' : 'Cuenta Secundaria'}
          {!(account as any).isOwner && ' · Compartida'}
        </p>
      </div>
      <div className="text-right flex-shrink-0">
        <p className="font-bold tabular-nums">{account.oro.toLocaleString('es-ES', { maximumFractionDigits: 0 })}</p>
        <p className="text-[0.65rem] text-muted-foreground">Oro</p>
      </div>
    </motion.button>
  );
};

// ─── Main page ────────────────────────────────────────────────────────────────

export default function JartinetBancoPage() {
  const [view, setView] = useState<'landing' | 'create' | 'createPrincipal' | 'login' | 'dashboard'>('landing');
  const [account, setAccount] = useState<BankAccount | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingTx, setLoadingTx] = useState(false);
  const [myAccounts, setMyAccounts] = useState<BankAccount[]>([]);
  const [loadingMyAccounts, setLoadingMyAccounts] = useState(true);

  const fetchMyAccounts = useCallback(async () => {
    setLoadingMyAccounts(true);
    const res = await getMyBankAccounts();
    if (res.success && res.accounts) setMyAccounts(res.accounts);
    setLoadingMyAccounts(false);
  }, []);

  useEffect(() => {
    fetchMyAccounts();
  }, [fetchMyAccounts]);

  const fetchTx = useCallback(async (iban: string) => {
    setLoadingTx(true);
    const res = await getTransactions(iban);
    if (res.success && res.transactions) setTransactions(res.transactions);
    setLoadingTx(false);
  }, []);

  useEffect(() => {
    if (account?.iban) fetchTx(account.iban);
  }, [account?.iban, fetchTx]);

  const enterAccount = (a: BankAccount) => {
    setAccount(a);
    setView('dashboard');
  };

  const handleLoginSuccess = (a: BankAccount) => {
    setAccount(a);
    setView('dashboard');
  };

  const handleLogout = () => {
    setAccount(null);
    setTransactions([]);
    setView('landing');
    fetchMyAccounts();
  };

  const handleAccountUpdate = (updates: Partial<BankAccount>) => {
    setAccount(prev => (prev ? { ...prev, ...updates } : prev));
  };

  if (view === 'dashboard' && account) {
    return (
      <div className="relative bg-background text-foreground min-h-screen w-full -m-4 md:-m-6 lg:-m-8">
        <BancoClient
          account={account}
          transactions={transactions}
          isLoadingTransactions={loadingTx}
          onTransferSuccess={() => fetchTx(account.iban)}
          onAccountUpdate={handleAccountUpdate}
          onLogout={handleLogout}
        />
      </div>
    );
  }

  return (
    <div className="relative bg-background text-foreground min-h-screen w-full -m-4 md:-m-6 lg:-m-8 overflow-hidden">
      <ParticleCanvas />
      <div className="absolute inset-0 bg-gradient-to-b from-background/85 via-background/55 to-background/85 pointer-events-none" />

      <AnimatePresence>
        {view === 'create' && <CreateAccountFlow onClose={() => { setView('landing'); fetchMyAccounts(); }} />}
        {view === 'createPrincipal' && <CreatePrincipalAccountFlow onClose={() => { setView('landing'); fetchMyAccounts(); }} />}
      </AnimatePresence>

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Minimal header */}
        <header className="flex items-center px-8 py-6">
          <motion.div
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2 text-2xl font-black text-primary"
          >
            <Landmark className="h-6 w-6" />
            <span>JartiBank</span>
          </motion.div>
        </header>

        {/* Hero area */}
        <div className="flex-1 flex items-center justify-center p-8">
          <AnimatePresence mode="wait">
            {view === 'landing' && (
              <motion.div
                key="hero"
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                className="text-center space-y-8 max-w-lg w-full"
              >
                {/* Pulsing icon */}
                <motion.div
                  animate={{
                    filter: [
                      'drop-shadow(0 0 16px rgba(234,179,8,0.3))',
                      'drop-shadow(0 0 40px rgba(234,179,8,0.65))',
                      'drop-shadow(0 0 16px rgba(234,179,8,0.3))',
                    ],
                  }}
                  transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
                >
                  <Landmark className="h-20 w-20 text-primary mx-auto" />
                </motion.div>

                <div>
                  <h1 className="text-6xl md:text-7xl font-black tracking-tight leading-none">
                    <span className="text-primary">Jarti</span>Bank
                  </h1>
                  <p className="text-muted-foreground mt-3 text-lg">Tu banco cofrade de confianza</p>
                </div>

                {loadingMyAccounts ? (
                  <div className="flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                ) : myAccounts.length > 0 && (
                  <div className="space-y-2 text-left">
                    <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground text-center">Tus cuentas</p>
                    {myAccounts.map(a => (
                      <MyAccountCard key={a.id} account={a} onSelect={() => enterAccount(a)} />
                    ))}
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    size="lg"
                    className="h-13 px-8 rounded-2xl text-base font-semibold shadow-lg shadow-primary/25"
                    onClick={() => setView('login')}
                  >
                    <LogIn className="mr-2 h-5 w-5" /> Iniciar Sesión
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-13 px-8 rounded-2xl text-base font-semibold border-white/15 bg-white/5 hover:bg-white/10"
                    onClick={() => setView('create')}
                  >
                    <UserPlus className="mr-2 h-5 w-5" /> Abrir Cuenta Secundaria
                  </Button>
                </div>
                <Button
                  size="lg"
                  variant="outline"
                  className="h-13 px-8 rounded-2xl text-base font-semibold border-amber-500/30 bg-amber-500/10 text-amber-400 hover:bg-amber-500/20"
                  onClick={() => setView('createPrincipal')}
                >
                  <Gamepad2 className="mr-2 h-5 w-5" /> Abrir Cuenta Principal (requiere estar en el server)
                </Button>

                <div className="flex flex-wrap justify-center gap-2">
                  {['Transferencias', 'Depósitos', 'Jartidex35'].map(f => (
                    <span key={f} className="px-3 py-1.5 text-xs rounded-full bg-white/5 border border-white/10 text-muted-foreground">
                      {f}
                    </span>
                  ))}
                </div>
              </motion.div>
            )}

            {view === 'login' && (
              <motion.div key="login" className="w-full max-w-sm">
                <LoginPanel onSuccess={handleLoginSuccess} onBack={() => setView('landing')} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
